import React from 'react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const milestones = [
    { year: 'THE VISION', title: 'Born from Heritage', description: 'QASR PARFUMERIE was founded with one mission: to bring the centuries-old art of Arabian perfumery to the world, honoring ancient traditions while embracing modern mastery.' },
    { year: 'REEF', title: 'The Riyadh Atelier', description: 'From the heart of Riyadh, Saudi Arabia, the Reef collection emerges — refined compositions that marry Arabian oud and saffron with contemporary elegance.' },
    { year: 'GISSAH', title: 'The Kuwait Legacy', description: 'Our partnership with Gissah, rooted in Kuwait City, brings a second lineage of masterful fragrance — bold, sophisticated, and unforgettable.' },
    { year: 'GLOBAL', title: 'Reaching New Shores', description: 'From the Arabian Peninsula to Australia and beyond, our fragrances now travel the same ancient trade routes that once carried frankincense and myrrh.' },
    { year: 'TODAY', title: 'Over 50 Fragrances', description: 'Each of our perfumes tells a story through ingredients sourced from over 30 countries — a living atlas of scent.' },
  ];

  const values = [
    {
      title: 'Craftsmanship',
      arabicTitle: 'الحرفية',
      description: 'Every fragrance is handcrafted using time-honored techniques. From the hand-harvesting of Kashmiri saffron to the slow distillation of Cambodian oud, we refuse to rush perfection.',
      image: 'https://images.unsplash.com/photo-1615529328331-f8917597711f?auto=format&fit=crop&q=80&w=800',
    },
    {
      title: 'Provenance',
      arabicTitle: 'الأصل',
      description: 'We trace every ingredient to its source. Turkish Damask rose, Iranian saffron, Indian sandalwood, French lavender — each element carries the soul of its homeland.',
      image: 'https://images.unsplash.com/photo-1608828201325-cfc4c1cc6ef9?auto=format&fit=crop&q=80&w=800',
    },
    {
      title: 'Heritage',
      arabicTitle: 'التراث',
      description: 'Arabian perfumery is one of the oldest traditions in the world. We carry this legacy forward, blending ancient wisdom with modern artistry to create something timeless.',
      image: 'https://images.unsplash.com/photo-1738414808975-201966230c59?auto=format&fit=crop&q=80&w=800',
    },
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1547887538-e3a2f32cb1cc?auto=format&fit=crop&q=80&w=1920"
            alt="Our Heritage"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#050505]/70" />
          <div className="absolute inset-0 arabian-pattern opacity-30" />
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-6">
            <p className="font-arabic text-3xl text-[#D4AF37]/60 mb-4">قصتنا</p>
            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-wide text-white mb-6">
              OUR <span className="text-[#D4AF37]">STORY</span>
            </h1>
            <p className="font-body text-white/60 max-w-2xl mx-auto text-base md:text-lg">
              The ancient art of Arabian perfumery, reimagined for the modern world
            </p>
          </div>
        </div>

        <div className="absolute top-24 left-8 w-32 h-32 border-t-2 border-l-2 border-[#D4AF37]/30" />
        <div className="absolute top-24 right-8 w-32 h-32 border-t-2 border-r-2 border-[#D4AF37]/30" />
      </section>

      {/* Introduction */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-heading text-xs tracking-[0.3em] text-[#D4AF37] mb-8">FROM ARABIA TO THE WORLD</p>
          <h2 className="font-heading text-3xl md:text-4xl tracking-wide text-white mb-8 leading-relaxed">
            QASR PARFUMERIE bridges two legendary houses of fragrance —
            <span className="text-[#D4AF37]"> Reef from Riyadh </span> and
            <span className="text-[#D4AF37]"> Gissah from Kuwait</span> — 
            bringing the finest Arabian perfumery to Australia.
          </h2>
          <p className="font-body text-white/60 leading-relaxed max-w-3xl mx-auto">
            For centuries, the Arabian Peninsula has been the crossroads of the world's most
            precious aromatic ingredients. Oud from Southeast Asia, saffron from Iran, roses
            from Turkey, sandalwood from India — all converging in the hands of master perfumers
            who transform raw materials into liquid art. QASR PARFUMERIE carries this tradition
            forward, curating fragrances that honor their origins while speaking to the modern soul.
          </p>
        </div>
      </section>

      {/* Two Houses */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16">
            {/* Reef */}
            <div className="text-center md:text-left">
              <div className="inline-block mb-4">
                <span className="font-heading text-[9px] tracking-[0.3em] px-4 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37]">REEF</span>
              </div>
              <h3 className="font-heading text-2xl tracking-wide text-white mb-4">THE RIYADH ATELIER</h3>
              <p className="font-body text-white/50 text-sm leading-relaxed mb-4">
                Born in the capital of Saudi Arabia, Reef draws from the deep well of Arabian perfumery tradition.
                Each composition is a testament to the art of layering — from the warmth of amber and oud to the
                brightness of bergamot and rose. The collection spans over 35 fragrances, from the iconic Reef 33
                with its bold saffron-oud signature to the delicate femininity of Summer Pink.
              </p>
              <p className="font-body text-white/40 text-sm leading-relaxed">
                Blended in Riyadh. Shipped to Australia. Worn by those who appreciate the extraordinary.
              </p>
            </div>
            {/* Gissah */}
            <div className="text-center md:text-left">
              <div className="inline-block mb-4">
                <span className="font-heading text-[9px] tracking-[0.3em] px-4 py-1.5 border border-[#8a6ec8]/40 text-[#8a6ec8]">GISSAH</span>
              </div>
              <h3 className="font-heading text-2xl tracking-wide text-white mb-4">THE KUWAIT LEGACY</h3>
              <p className="font-body text-white/50 text-sm leading-relaxed mb-4">
                From Kuwait City, Gissah represents the pinnacle of Gulf perfumery. These are fragrances that
                command attention — Imperial Valley's starlit elegance, Hudson Valley's modern sophistication,
                Mavro's bold power. Presented in 200ml bottles, each Gissah creation is designed to be a
                statement, not just a scent.
              </p>
              <p className="font-body text-white/40 text-sm leading-relaxed">
                Crafted in Kuwait. Journeying through 30+ countries for ingredients. Destined for Sydney.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-arabic text-2xl text-[#D4AF37]/60 mb-2">قيمنا</p>
            <h2 className="font-heading text-4xl tracking-wide text-white">
              OUR <span className="text-[#D4AF37]">VALUES</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value) => (
              <div key={value.title} className="group">
                <div className="relative aspect-[4/5] overflow-hidden mb-6 border border-[#D4AF37]/10 group-hover:border-[#D4AF37]/40 transition-colors duration-300">
                  <img
                    src={value.image}
                    alt={value.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="font-arabic text-xl text-[#D4AF37]/60 mb-1">{value.arabicTitle}</p>
                    <h3 className="font-heading text-2xl text-white">{value.title}</h3>
                  </div>
                </div>
                <p className="font-body text-white/60 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#D4AF37]/20 hidden md:block" />

        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="font-arabic text-2xl text-[#D4AF37]/60 mb-2">رحلتنا</p>
            <h2 className="font-heading text-4xl tracking-wide text-white">
              OUR <span className="text-[#D4AF37]">JOURNEY</span>
            </h2>
          </div>

          <div className="space-y-16">
            {milestones.map((milestone, index) => (
              <div
                key={milestone.year}
                className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row flex-col' : 'md:flex-row-reverse flex-col'}`}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right text-center' : 'md:text-left text-center'}`}>
                  <p className="font-heading text-2xl md:text-3xl text-[#D4AF37] mb-2">{milestone.year}</p>
                  <h3 className="font-heading text-lg text-white mb-2">{milestone.title}</h3>
                  <p className="font-body text-white/50 text-sm">{milestone.description}</p>
                </div>
                <div className="w-4 h-4 bg-[#D4AF37] rounded-full relative z-10 flex-shrink-0 hidden md:block" />
                <div className="flex-1 hidden md:block" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ingredient Map Callout */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-heading text-[10px] tracking-[0.3em] text-[#D4AF37]/50 mb-4">INGREDIENTS FROM 30+ COUNTRIES</p>
          <h2 className="font-heading text-2xl md:text-3xl tracking-wide text-white/80 mb-6">
            Every bottle is a journey across continents
          </h2>
          <p className="font-body text-white/40 text-sm max-w-2xl mx-auto leading-relaxed mb-8">
            From the saffron fields of Iran to the rose valleys of Turkey, from Cambodian oud forests to 
            Italian bergamot groves — each ingredient is traced back to its origin and sourced with care.
            Explore the interactive map on our homepage to see exactly where your fragrance comes from.
          </p>
          <Link to="/" className="inline-block">
            <span className="font-heading text-[10px] tracking-[0.2em] text-[#D4AF37] border-b border-[#D4AF37]/30 pb-1 hover:border-[#D4AF37] transition-colors">
              EXPLORE THE INGREDIENT MAP
            </span>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-arabic text-2xl text-[#D4AF37]/60 mb-4">اكتشف</p>
          <h2 className="font-heading text-4xl md:text-5xl tracking-wide text-white mb-8">
            DISCOVER OUR <span className="text-[#D4AF37]">COLLECTION</span>
          </h2>
          <p className="font-body text-white/60 mb-12 max-w-2xl mx-auto">
            Explore over 50 handcrafted fragrances from two legendary Arabian houses, 
            each one a journey waiting to unfold.
          </p>
          <Link to="/products">
            <button className="bg-[#D4AF37] text-[#050505] px-12 py-5 font-heading text-sm tracking-[0.2em] hover:bg-[#E5C158] transition-colors duration-200" data-testid="about-explore-btn">
              EXPLORE FRAGRANCES
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
